<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterGeneralPanel',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_2095089031469157_emit_l6mwfw734_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data.modalResult.value,
    };
  };
  const inst_evh_2095089031469157_close_spq7zljkc_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_0tz0yrght_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterEditor',
      props: {
        meter: props.meter.editor(),
      },
    };
  };
  const act_3818132362335583_emit_hbmei12tn_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_7315092382398562_reloadComponentData_rftsarr01 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_hv24orda9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_rftsarr01,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_hv24orda9],
    event: 'reload',
  });
  const act_2095089031469157_emit_l6mwfw734 = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_l6mwfw734_getActionArgs,
  });
  const inst_evh_2095089031469157_close_spq7zljkc =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_l6mwfw734,
      event: 'close',
      when: inst_evh_2095089031469157_close_spq7zljkc_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_0tz0yrght =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_0tz0yrght_getActionArgs,
      events: [inst_evh_2095089031469157_close_spq7zljkc],
    });
  const inst_evh_2095089031469157_clickPreventStop_0f4gnmh5p =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_0tz0yrght,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_0f4gnmh5p],
      event: 'click.prevent.stop',
    });
  const act_3818132362335583_emit_hbmei12tn = new actionTypes.EmitAction({
    actionArgs: act_3818132362335583_emit_hbmei12tn_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_3818132362335583_reload_97m8k0tbi =
    new actionTypes.EventHandlerImpl({
      action: act_3818132362335583_emit_hbmei12tn,
      event: 'reload',
      displayName: 'emitReload',
    });
  const inst_evh_3818132362335583_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3818132362335583_reload_97m8k0tbi],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_hv24orda9,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_spq7zljkc,
    inst_evh_2095089031469157_clickPreventStop_0f4gnmh5p,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_3818132362335583_reload_97m8k0tbi,
    emitReload: inst_evh_3818132362335583_reload_97m8k0tbi,
    inst_evh_3818132362335583_reload,
  };

  const evh_7315092382398562_reload_hv24orda9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_hv24orda9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_spq7zljkc = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_spq7zljkc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_0f4gnmh5p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_0f4gnmh5p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3818132362335583_reload_97m8k0tbi = async (event, scope) => {
    return await actions.inst_evh_3818132362335583_reload_97m8k0tbi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3818132362335583_reload = async (event, scope) => {
    return await actions.inst_evh_3818132362335583_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Meter', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem :label="pgettext('Meter', 'Type')">
          <ItpBox alignItems="center">
            <ItpBadge no-box-shadow variant="light" class="me-2">
              <UcMeterTypeIcon :type="meter.meter_type"> </UcMeterTypeIcon>
              <ItpText
                :text="pgettext('MeterTypes', meter.meter_type, 'Sentence')"
                class="mx-2"
              >
              </ItpText>
            </ItpBadge>
            <ItpBadge
              :title="pgettext('Meter', 'Unit')"
              no-box-shadow
              variant="light"
              class="mx-2"
            >
              <ItpText :text="meter.unit" class="ps-2"> </ItpText>
            </ItpBadge>
            <ItpBadge
              :title="pgettext('Meter', 'Multiplier')"
              no-box-shadow
              variant="light"
              class="mx-2"
            >
              <ItpIcon name="fa-times"> </ItpIcon>
              <ItpText :text="number(meter.multiplier)" class="ps-2"> </ItpText>
            </ItpBadge>
          </ItpBox>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Main meter')"
          v-if="meter.main_meter"
        >
          <UcCompactMeterDisplay :payload="meter.main_meter">
          </UcCompactMeterDisplay>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Serial number')"
          :content="meter.serial_number"
          :helpTooltip="gettext('The device\'s unique serial number.')"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Smart device')"
          :content="gettext('Yes')"
          v-if="meter.smart_device"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Groups')"
          v-if="meter.groups && meter.groups.length"
        >
          <ItpBadge
            :key="name"
            :text="name"
            class="border border-gray-dark-900 me-2"
            v-for="name in meter.groups"
          >
          </ItpBadge>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Meter', 'Status')">
          <UcResourceStatusCard
            :resource="meter"
            @reload="evh_3818132362335583_reload($event, {})"
          >
          </UcResourceStatusCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Model name')"
          :content="meter.model_name"
          v-if="meter.model_name"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'DevEUI')"
          :content="meter.deveui"
          :helpTooltip="
            gettext(
              '64-bit globally-unique Extended Unique Identifier (EUI-64) assigned by the manufacturer, or the owner, of the end-device.'
            )
          "
          v-if="meter.deveui"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Location Code')"
          :content="meter.tag_id"
          :helpTooltip="
            gettext('Locally-unique tag ID to be used within the company.')
          "
          v-if="meter.tag_id"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Meter', 'Validity')">
          <UcMeterValidityCard :item="meter"> </UcMeterValidityCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Site')"
          v-if="meter.site"
        >
          <ItpLink
            :to="{
              name: 'app.property',
              params: { id: meter.site.id },
            }"
          >
            {{ meter.site.name }}
            <span v-if="meter.site.description"
              >- {{ meter.site.description }}</span
            >
          </ItpLink>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Description')"
          :content="meter.description"
          v-if="meter.description"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
